import { supabase } from '../lib/supabase';

/**
 * Abidjan Distribution Service - Container & Delivery Management
 * Manages Abidjan-specific container distribution and delivery task operations
 */
export const abidjanDistributionService = {
  /**
   * Get all containers destined for Abidjan with delivery statistics
   * @param {Object} filters - Optional filters { status, dateFrom, dateTo }
   * @returns {Promise<Array>} Array of containers with delivery metrics
   */
  async getAbidjanContainers(filters = {}) {
    try {
      let query = supabase
        ?.from('containers')
        ?.select(`
          *,
          delivery_tasks(
            id,
            status,
            incident_type
          )
        `)
        ?.eq('destination_port', 'Abidjan')
        ?.order('created_at', { ascending: false });

      // Apply status filter
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      // Apply date range filters
      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate delivery statistics for each container
      return data?.map(container => {
        const deliveryTasks = container?.delivery_tasks || [];
        const totalDeliveries = deliveryTasks?.length;
        const deliveredCount = deliveryTasks?.filter(task => task?.status === 'LIVRE')?.length;
        const incidentCount = deliveryTasks?.filter(task => task?.status === 'INCIDENT')?.length;

        // Map status to French labels
        let statusLabel = '';
        switch (container?.status) {
          case 'draft': case'open': case'warehouse_abidjan':
            statusLabel = 'EN_ATTENTE';
            break;
          case 'en_distribution':
            statusLabel = 'EN_DISTRIBUTION';
            break;
          case 'termine':
            statusLabel = 'TERMINE';
            break;
          default:
            statusLabel = container?.status?.toUpperCase();
        }

        return {
          id: container?.id,
          containerCode: container?.container_code,
          vesselName: container?.vessel_name,
          shippingLine: container?.shipping_line,
          departurePort: container?.departure_port,
          destinationPort: container?.destination_port,
          departureDate: container?.departure_date,
          etaDate: container?.eta_date,
          status: container?.status,
          statusLabel,
          totalDeliveries,
          deliveredCount,
          incidentCount,
          createdAt: container?.created_at,
          updatedAt: container?.updated_at
        };
      }) || [];
    } catch (error) {
      console.error('❌ Error fetching Abidjan containers:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des conteneurs Abidjan');
    }
  },

  /**
   * Get container detail with all associated delivery tasks
   * NOW WITH COMMUNE/QUARTIER GROUPING for Abidjan automatic organization
   * @param {string} containerId - Container UUID
   * @returns {Promise<Object>} Container with delivery tasks grouped by commune/quartier
   */
  async getContainerDetail(containerId) {
    try {
      const { data: container, error: containerError } = await supabase
        ?.from('containers')
        ?.select('*')
        ?.eq('id', containerId)
        ?.single();

      if (containerError) throw containerError;

      // Fetch delivery tasks with related shipment data (including commune/quartier)
      const { data: deliveryTasks, error: tasksError } = await supabase
        ?.from('delivery_tasks')
        ?.select(`
          *,
          assigned_employee:employees!assigned_employee_id(
            id,
            name,
            email,
            phone,
            zone
          ),
          shipment:shipments(
            id,
            reference,
            sender_name,
            receiver_name,
            receiver_phone,
            delivery_commune,
            delivery_quartier
          )
        `)
        ?.eq('container_id', containerId)
        ?.order('delivery_zone', { ascending: true })
        ?.order('route_order', { ascending: true, nullsFirst: false });

      if (tasksError) throw tasksError;

      // Transform delivery tasks data with commune/quartier
      const tasks = deliveryTasks?.map(task => {
        const commune = task?.shipment?.delivery_commune || 'Commune non renseignée';
        const quartier = task?.shipment?.delivery_quartier || 'Quartier non renseigné';
        
        return {
          id: task?.id,
          recipientName: task?.recipient_name,
          recipientPhone: task?.recipient_phone,
          deliveryAddress: task?.delivery_address,
          deliveryCommune: commune,
          deliveryQuartier: quartier,
          deliveryZone: task?.delivery_zone,
          routeOrder: task?.route_order,
          status: task?.status,
          incidentType: task?.incident_type,
          incidentComment: task?.incident_comment,
          plannedDate: task?.planned_date,
          plannedTimeWindow: task?.planned_time_window,
          deliveryNotes: task?.delivery_notes,
          createdAt: task?.created_at,
          updatedAt: task?.updated_at,
          agent: task?.assigned_employee ? {
            id: task?.assigned_employee?.id,
            name: task?.assigned_employee?.name,
            email: task?.assigned_employee?.email,
            phone: task?.assigned_employee?.phone,
            zone: task?.assigned_employee?.zone
          } : null,
          shipment: task?.shipment ? {
            id: task?.shipment?.id,
            reference: task?.shipment?.reference,
            senderName: task?.shipment?.sender_name,
            receiverName: task?.shipment?.receiver_name,
            receiverPhone: task?.shipment?.receiver_phone
          } : null
        };
      }) || [];

      // Group by commune and quartier for organized view
      const grouped = {};
      tasks?.forEach(task => {
        const commune = task?.deliveryCommune;
        const quartier = task?.deliveryQuartier;

        if (!grouped?.[commune]) {
          grouped[commune] = {};
        }

        if (!grouped?.[commune]?.[quartier]) {
          grouped[commune][quartier] = {
            tasks: [],
            counters: {
              total: 0,
              LIVRE: 0,
              INCIDENT: 0,
              remaining: 0
            }
          };
        }

        grouped?.[commune]?.[quartier]?.tasks?.push(task);
        grouped[commune][quartier].counters.total++;

        if (task?.status === 'LIVRE') {
          grouped[commune][quartier].counters.LIVRE++;
        } else if (task?.status === 'INCIDENT') {
          grouped[commune][quartier].counters.INCIDENT++;
        } else {
          grouped[commune][quartier].counters.remaining++;
        }
      });

      // Sort tasks within each quartier by status priority
      const statusPriority = { 'A_PREPARER': 1, 'EN_LIVRAISON': 2, 'INCIDENT': 3, 'LIVRE': 4 };
      
      Object?.keys(grouped)?.forEach(commune => {
        Object?.keys(grouped?.[commune])?.forEach(quartier => {
          grouped?.[commune]?.[quartier]?.tasks?.sort((a, b) => {
            const priorityA = statusPriority?.[a?.status] || 5;
            const priorityB = statusPriority?.[b?.status] || 5;
            return priorityA - priorityB;
          });
        });
      });

      // Calculate container statistics
      const totalDeliveries = tasks?.length;
      const deliveredCount = tasks?.filter(t => t?.status === 'LIVRE')?.length;
      const incidentCount = tasks?.filter(t => t?.status === 'INCIDENT')?.length;

      return {
        container: {
          id: container?.id,
          containerCode: container?.container_code,
          vesselName: container?.vessel_name,
          shippingLine: container?.shipping_line,
          departurePort: container?.departure_port,
          destinationPort: container?.destination_port,
          departureDate: container?.departure_date,
          etaDate: container?.eta_date,
          status: container?.status,
          notes: container?.notes,
          totalDeliveries,
          deliveredCount,
          incidentCount,
          createdAt: container?.created_at,
          updatedAt: container?.updated_at
        },
        deliveryTasks: tasks,
        grouped: grouped
      };
    } catch (error) {
      console.error('❌ Error fetching container detail:', error);
      throw new Error(error?.message || 'Erreur lors du chargement du détail conteneur');
    }
  },

  /**
   * Get all delivery tasks with INCIDENT status for Abidjan
   * @param {Object} filters - Optional filters { containerId, agentId, incidentType }
   * @returns {Promise<Array>} Array of incident delivery tasks
   */
  async getIncidentDeliveryTasks(filters = {}) {
    try {
      let query = supabase
        ?.from('delivery_tasks')
        ?.select(`
          *,
          assigned_employee:employees!assigned_employee_id(
            id,
            name,
            email,
            phone,
            zone
          ),
          container:containers(
            id,
            container_code,
            vessel_name
          ),
          shipment:shipments(
            id,
            reference,
            sender_name,
            receiver_name
          )
        `)
        ?.eq('status', 'INCIDENT')
        ?.order('updated_at', { ascending: false });

      // Apply container filter
      if (filters?.containerId) {
        query = query?.eq('container_id', filters?.containerId);
      }

      // Apply agent filter
      if (filters?.agentId) {
        query = query?.eq('assigned_employee_id', filters?.agentId);
      }

      // Apply incident type filter
      if (filters?.incidentType) {
        query = query?.eq('incident_type', filters?.incidentType);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data for admin display
      return data?.map(task => ({
        id: task?.id,
        recipientName: task?.recipient_name,
        recipientPhone: task?.recipient_phone,
        deliveryAddress: task?.delivery_address,
        deliveryZone: task?.delivery_zone,
        incidentType: task?.incident_type,
        incidentComment: task?.incident_comment,
        plannedDate: task?.planned_date,
        plannedTimeWindow: task?.planned_time_window,
        createdAt: task?.created_at,
        updatedAt: task?.updated_at,
        agent: task?.assigned_employee ? {
          id: task?.assigned_employee?.id,
          name: task?.assigned_employee?.name,
          email: task?.assigned_employee?.email,
          phone: task?.assigned_employee?.phone,
          zone: task?.assigned_employee?.zone
        } : null,
        container: task?.container ? {
          id: task?.container?.id,
          containerCode: task?.container?.container_code,
          vesselName: task?.container?.vessel_name
        } : null,
        shipment: task?.shipment ? {
          id: task?.shipment?.id,
          reference: task?.shipment?.reference,
          senderName: task?.shipment?.sender_name,
          receiverName: task?.shipment?.receiver_name
        } : null
      })) || [];
    } catch (error) {
      console.error('❌ Error fetching incident delivery tasks:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des incidents de livraison');
    }
  },

  /**
   * Get all active Abidjan agents for filtering
   * @returns {Promise<Array>} Array of agents
   */
  async getAbidjanAgents() {
    try {
      const { data, error } = await supabase
        ?.from('employees')
        ?.select('id, name, email, phone, zone')
        ?.eq('role', 'AGENT_ABJ')
        ?.eq('is_active', true)
        ?.order('name', { ascending: true });

      if (error) throw error;

      return data?.map(agent => ({
        id: agent?.id,
        name: agent?.name,
        email: agent?.email,
        phone: agent?.phone,
        zone: agent?.zone
      })) || [];
    } catch (error) {
      console.error('❌ Error fetching Abidjan agents:', error);
      throw new Error(error?.message || 'Erreur lors du chargement des agents Abidjan');
    }
  },

  /**
   * Resolve incident - revert delivery task from INCIDENT to A_PREPARER
   * @param {string} deliveryTaskId - Delivery task UUID
   * @returns {Promise<Object>} Updated delivery task
   */
  async resolveIncident(deliveryTaskId) {
    try {
      const { data, error } = await supabase
        ?.from('delivery_tasks')
        ?.update({
          status: 'A_PREPARER',
          incident_type: null,
          incident_comment: null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', deliveryTaskId)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('❌ Error resolving incident:', error);
      throw new Error(error?.message || 'Erreur lors de la résolution de l\'incident');
    }
  },

  /**
   * Reassign delivery task to another agent
   * @param {string} deliveryTaskId - Delivery task UUID
   * @param {string} newAgentId - New agent UUID
   * @returns {Promise<Object>} Updated delivery task
   */
  async reassignDeliveryTask(deliveryTaskId, newAgentId) {
    try {
      const { data, error } = await supabase
        ?.from('delivery_tasks')
        ?.update({
          assigned_employee_id: newAgentId,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', deliveryTaskId)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('❌ Error reassigning delivery task:', error);
      throw new Error(error?.message || 'Erreur lors de la réassignation');
    }
  }
};